import { Button } from '@/components/common/button'
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogContentWrapper,
  DialogHeader,
  DialogHeaderWrapper,
  DialogScrollArea,
  DialogTitle,
  DialogTrigger,
} from '@/components/common/dialog'
import { ExpansionArrowsIcon } from '@/components/icons/expansionArrows'
import type { HistoricalData } from '@/queries/historical.query'
import type { VaultApyRange } from '@/types/apy'
import type { Vault } from '@/types/vault'
import type { ReactNode } from 'react'
import { SharePricePerformance } from './performance/sharePrice'
import { TvlPerformance } from './performance/tvl'
import { YieldPerformance } from './performance/yield'

export interface PerformanceChartsProps {
  vault: Vault
  apyRange: VaultApyRange
  includeRewards: boolean
  intrinsicApy: number | null
  chartData: HistoricalData
  latestSharePrice: number | null
  sharePricePeriodChange: number | null
  tvlPeriodChange: number | null
  rangeSelector: ReactNode
}

export const PerformanceCharts = ({
  vault,
  apyRange,
  includeRewards,
  intrinsicApy,
  chartData,
  latestSharePrice,
  sharePricePeriodChange,
  tvlPeriodChange,
  rangeSelector,
}: PerformanceChartsProps) => {
  const yieldPerformanceProps = {
    vault,
    apyRange,
    includeRewards,
    intrinsicApy,
    chartData,
  }
  const tvlPerformanceProps = { vault, chartData, tvlPeriodChange }
  const sharePricePerformanceProps = {
    chartData,
    latestSharePrice,
    periodChange: sharePricePeriodChange,
  }

  return (
    <div className="flex flex-col gap-4">
      <PerformanceChart
        rangeSelector={rangeSelector}
        title="Yield"
        fullView={
          <YieldPerformance
            {...yieldPerformanceProps}
            chartClassName="h-80 min-h-80"
            rangeSelector={rangeSelector}
            showBrush
          />
        }
      >
        <YieldPerformance {...yieldPerformanceProps} chartClassName="h-80 min-h-80" />
      </PerformanceChart>
      <PerformanceChart
        rangeSelector={rangeSelector}
        title="Share price"
        fullView={
          <SharePricePerformance
            {...sharePricePerformanceProps}
            chartClassName="h-80 min-h-80"
            rangeSelector={rangeSelector}
            showBrush
          />
        }
      >
        <SharePricePerformance {...sharePricePerformanceProps} chartClassName="h-80 min-h-80" />
      </PerformanceChart>
      <PerformanceChart
        rangeSelector={rangeSelector}
        title="TVL"
        fullView={
          <TvlPerformance
            {...tvlPerformanceProps}
            chartClassName="h-80 min-h-80"
            rangeSelector={rangeSelector}
            showBrush
          />
        }
      >
        <TvlPerformance {...tvlPerformanceProps} chartClassName="h-80 min-h-80" />
      </PerformanceChart>
    </div>
  )
}

interface PerformanceChartProps {
  title: string
  children: ReactNode
  fullView: ReactNode
  rangeSelector: ReactNode
}

const PerformanceChart = ({ title, children, fullView, rangeSelector }: PerformanceChartProps) => (
  <section className="flex flex-col gap-3">
    {children}
    <div className="flex items-center justify-between gap-2">
      {rangeSelector}
      <Dialog>
        <DialogTrigger asChild>
          <Button
            aria-label={`Show ${title} in full view`}
            variant="default"
            size="default"
            className="flex items-center gap-1 rounded-full text-body-foreground text-sm"
          >
            Show in full view
            <ExpansionArrowsIcon expanded={false} size={16} />
          </Button>
        </DialogTrigger>
        <DialogContent hideCloseButton className="max-w-5xl bg-page lg:max-h-[90vh]">
          <DialogBody>
            <DialogHeaderWrapper className="gap-0 pt-6 md:pt-6">
              <DialogHeader className="flex flex-col">
                <DialogTitle className="sr-only">{title} - detailed view</DialogTitle>
                <nav className="mb-4 ml-auto w-fit">
                  <Button size="default" variant="default" className="gap-1 rounded-full text-sm" asChild>
                    <DialogClose>
                      Go to default view
                      <ExpansionArrowsIcon expanded size={16} />
                    </DialogClose>
                  </Button>
                </nav>
              </DialogHeader>
            </DialogHeaderWrapper>
            <DialogScrollArea>
              <DialogContentWrapper className="pt-0 md:pt-0">{fullView}</DialogContentWrapper>
            </DialogScrollArea>
          </DialogBody>
        </DialogContent>
      </Dialog>
    </div>
  </section>
)
